import { useState, useEffect, useRef } from "react";
import {
  Stethoscope,
  AlertCircle,
  ClipboardCheck,
  ArrowRight,
  AlertTriangle,
  Activity,
  MessageCircle,
  Info,
  Shield,
  CheckCircle,
  Calendar,
  Zap,
  PawPrint,
  Search,
  ChevronDown,
  ChevronUp,
  ListChecks,
  Filter,
  Share2,
  Download,
  Star,
  Sparkles,
  AlertOctagon,
  FileText,
  Printer,
  Bell
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import UpgradeModalVetConsult from '../UpgradeModalVetConsult';
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SignedIn, SignedOut, UserButton, SignIn, useUser } from "@clerk/clerk-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const checkPremiumStatus = (user) => {
  if (!user) return false;
  if (!user.unsafeMetadata) return false;
  return user.unsafeMetadata.isPremium === true;
};

// Custom progress indicator based on probability
const ProbabilityIndicator = ({ probability, color }) => {
  // Convert string probability to numeric value
  const getNumericValue = (prob) => {
    if (prob.toLowerCase().includes('muy alta')) return 90;
    if (prob.toLowerCase().includes('alta')) return 75;
    if (prob.toLowerCase().includes('media')) return 50;
    if (prob.toLowerCase().includes('baja')) return 25;
    if (prob.toLowerCase().includes('muy baja')) return 10;
    return 50; // Default
  };

  const value = getNumericValue(probability);

  return (
    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full ${color}`}
        style={{ width: `${value}%` }}
      ></div>
    </div>
  );
};

const DiagnosticoItem = ({ diagnostico, config, isOpen, toggleOpen, index, printMode }) => {
  const AlertIcon = config.alertIcon;
  const itemRef = useRef(null);

  // Expanded view is always shown in print mode
  const showExpanded = isOpen || printMode;

  return (
    <div
      ref={itemRef}
      className={cn(
        "border-b last:border-b-0 transition-all duration-300",
        config.borderColor,
        isOpen ? "shadow-md" : ""
      )}
    >
      {/* Accordion Header - Always visible */}
      <div
        className={cn(
          "p-4 flex items-center justify-between cursor-pointer transition-colors",
          isOpen ? config.headerBg : "bg-white hover:bg-gray-50/80"
        )}
        onClick={() => !printMode && toggleOpen()}
        aria-expanded={isOpen}
        aria-controls={`diagnostic-content-${index}`}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            "h-9 w-9 rounded-full flex items-center justify-center border shadow-sm flex-shrink-0",
            config.badge
          )}>
            <AlertIcon className={cn("h-4 w-4", config.iconColor)} />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className={cn("font-medium truncate max-w-[280px] sm:max-w-md", config.titleColor)}>
              {diagnostico.condicion}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={cn(
                "px-2 py-0.5 rounded-full text-xs font-medium",
                config.badge
              )}>
                {diagnostico.probabilidad}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {config.alertBadge && (
            <div className={cn(
              "rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1.5",
              config.badge
            )}>
              {config.pulseAnimation && (
                <span className="relative flex h-2 w-2 mr-1">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
              )}
              {config.alertBadge}
            </div>
          )}

          {!printMode && (
            <div className="bg-white p-1 rounded-full shadow-sm border border-gray-200">
              {isOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expandable Content */}
      {showExpanded && (
        <div
          id={`diagnostic-content-${index}`}
          className={cn("p-5 space-y-5 border-t", config.bgGradient)}
        >
          {/* Probability Indicator */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm text-gray-600 font-medium">Probabilidad</span>
              <span className={`text-sm font-medium ${config.iconColor}`}>
                {diagnostico.probabilidad}
              </span>
            </div>
            <ProbabilityIndicator
              probability={diagnostico.probabilidad}
              color={config.progressColor}
            />
          </div>

          {/* Grid de información */}
          <div className="grid sm:grid-cols-2 gap-6">
            {/* Síntomas */}
            <div className={cn("rounded-xl p-4 border", config.boxBg, config.boxBorder)}>
              <h4 className="text-base font-medium text-gray-900 flex items-center gap-2 mb-3">
                <div className={cn("h-7 w-7 rounded-full flex items-center justify-center", config.badge)}>
                  <Activity className={cn("h-4 w-4", config.iconColor)} />
                </div>
                Síntomas observados:
              </h4>
              <div className="space-y-2.5">
                {diagnostico.sintomas.map((sintoma, idx) => (
                  <div key={idx}
                    className="flex items-start gap-3 p-1.5 hover:bg-white/80 rounded-lg transition-colors">
                    <span className={cn(
                      "w-3 h-3 rounded-full mt-1 flex-shrink-0",
                      config.dotColor
                    )} />
                    <span className="text-gray-700">{sintoma}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Próximos pasos */}
            <div className="bg-green-50/30 rounded-xl p-4 border border-green-100">
              <h4 className="text-base font-medium text-gray-900 flex items-center gap-2 mb-3">
                <div className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center border border-green-200">
                  <ClipboardCheck className="h-4 w-4 text-green-600" />
                </div>
                Próximos pasos:
              </h4>
              <div className="space-y-2.5">
                {diagnostico.siguientesPasos.map((paso, idx) => (
                  <div key={idx}
                    className="flex items-start gap-3 p-1.5 hover:bg-white/80 rounded-lg transition-colors">
                    <div className="h-5 w-5 bg-white rounded-full flex items-center justify-center shadow-sm border border-green-200">
                      <ArrowRight className="h-3 w-3 text-green-500" />
                    </div>
                    <span className="text-gray-700">{paso}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Razonamiento clínico */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="h-6 w-6 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-200 flex-shrink-0 mt-0.5">
                <Search className="h-3.5 w-3.5 text-gray-600" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-1">Razonamiento clínico:</h4>
                <p className="text-gray-700">
                  {diagnostico.razonamientoClinico}
                </p>
              </div>
            </div>
          </div>

          {/* Action buttons for this diagnosis */}
        </div>
      )}
    </div>
  );
};

const DiagnosticosCard = ({ diagnosticosADescartar }) => {
  if (!diagnosticosADescartar?.length) return null;

  const { user, isLoaded, isSignedIn } = useUser();
  const isPremium = checkPremiumStatus(user);
  const [showUpgradeModalVetConsult, setShowUpgradeModalVetConsult] = useState(false);
  const [activeTab, setActiveTab] = useState("todos");
  const [printMode, setPrintMode] = useState(false);

  // State to track which diagnostic items are expanded
  const [openDiagnostics, setOpenDiagnostics] = useState({});

  // Filter diagnostics based on selected tab
  const getFilteredDiagnostics = () => {
    if (activeTab === "todos") return diagnosticosADescartar;
    if (activeTab === "urgentes") return diagnosticosADescartar.filter(d => d.urgencia === 'rojo' || d.urgencia === 'naranja');
    if (activeTab === "monitorear") return diagnosticosADescartar.filter(d => d.urgencia === 'amarillo');
    if (activeTab === "normales") return diagnosticosADescartar.filter(d => d.urgencia === 'verde');
    return diagnosticosADescartar;
  };

  const filteredDiagnostics = getFilteredDiagnostics();

  // Initialize with the first and most urgent items open
  useEffect(() => {
    if (diagnosticosADescartar?.length) {
      const initialState = {};
      // Open the first item by default
      initialState[0] = true;

      // Also open the most urgent items (up to 2 total)
      let urgentCount = 0;
      diagnosticosADescartar.forEach((diag, idx) => {
        if (idx > 0 && (diag.urgencia === 'rojo' || diag.urgencia === 'naranja') && urgentCount < 1) {
          initialState[idx] = true;
          urgentCount++;
        }
      });

      setOpenDiagnostics(initialState);
    }
  }, [diagnosticosADescartar]);

  const toggleDiagnostic = (idx) => {
    setOpenDiagnostics(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  // Function to expand/collapse all items
  const toggleAllDiagnostics = (expand) => {
    const newState = {};
    diagnosticosADescartar.forEach((_, idx) => {
      newState[idx] = expand;
    });
    setOpenDiagnostics(newState);
  };

  // Function to prepare for printing
  const handlePrint = () => {
    setPrintMode(true);
    // Wait for state to update before printing
    setTimeout(() => {
      window.print();
      // Reset print mode after printing
      setTimeout(() => {
        setPrintMode(false);
      }, 500);
    }, 200);
  };

  const urgencyConfig = {
    rojo: {
      borderColor: 'border-l-4 border-l-red-500',
      bgGradient: 'bg-gradient-to-r from-red-50 to-white',
      headerBg: 'bg-red-50',
      iconColor: 'text-red-600',
      badge: 'bg-red-100 border border-red-200 text-red-700',
      dotColor: 'bg-red-400',
      shadowColor: 'shadow-red-100',
      pulseAnimation: true,
      titleColor: 'text-red-800',
      boxBg: 'bg-red-50/30',
      boxBorder: 'border-red-100',
      alertIcon: AlertOctagon,
      alertBadge: 'URGENTE',
      progressColor: 'bg-red-500'
    },
    naranja: {
      borderColor: 'border-l-4 border-l-orange-500',
      bgGradient: 'bg-gradient-to-r from-orange-50 to-white',
      headerBg: 'bg-orange-50',
      iconColor: 'text-orange-600',
      badge: 'bg-orange-100 border border-orange-200 text-orange-700',
      dotColor: 'bg-orange-400',
      shadowColor: 'shadow-orange-100',
      pulseAnimation: false,
      titleColor: 'text-orange-800',
      boxBg: 'bg-orange-50/30',
      boxBorder: 'border-orange-100',
      alertIcon: AlertTriangle,
      alertBadge: 'ATENCIÓN',
      progressColor: 'bg-orange-500'
    },
    amarillo: {
      borderColor: 'border-l-4 border-l-yellow-500',
      bgGradient: 'bg-gradient-to-r from-yellow-50 to-white',
      headerBg: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
      badge: 'bg-yellow-100 border border-yellow-200 text-yellow-700',
      dotColor: 'bg-yellow-400',
      shadowColor: 'shadow-yellow-100',
      pulseAnimation: false,
      titleColor: 'text-yellow-800',
      boxBg: 'bg-yellow-50/30',
      boxBorder: 'border-yellow-100',
      alertIcon: Info,
      alertBadge: 'MONITOREAR',
      progressColor: 'bg-yellow-500'
    },
    verde: {
      borderColor: 'border-l-4 border-l-green-500',
      bgGradient: 'bg-gradient-to-r from-green-50 to-white',
      headerBg: 'bg-green-50',
      iconColor: 'text-green-600',
      badge: 'bg-green-100 border border-green-200 text-green-700',
      dotColor: 'bg-green-400',
      shadowColor: 'shadow-green-100',
      pulseAnimation: false,
      titleColor: 'text-green-800',
      boxBg: 'bg-green-50/30',
      boxBorder: 'border-green-100',
      alertIcon: CheckCircle,
      alertBadge: '',
      progressColor: 'bg-green-500'
    }
  };

  const hasUrgentDiagnostics = diagnosticosADescartar?.some(
    diagnostico => diagnostico.urgencia === 'amarillo' || diagnostico.urgencia === 'rojo' || diagnostico.urgencia === 'naranja'
  );

  const urgentCount = diagnosticosADescartar?.filter(
    diagnostico => diagnostico.urgencia === 'rojo' || diagnostico.urgencia === 'naranja'
  ).length;

  const monitorCount = diagnosticosADescartar?.filter(
    diagnostico => diagnostico.urgencia === 'amarillo'
  ).length;

  const normalCount = diagnosticosADescartar?.filter(
    diagnostico => diagnostico.urgencia === 'verde'
  ).length;

  async function onSubmit(event) {
    event.preventDefault();

    if (!isPremium) {
      setShowUpgradeModalVetConsult(true);
      return;
    }

    const message = encodeURIComponent(
      "Hola, me gustaría agendar una consulta veterinaria."
    );
    window.open(`https://wa.me/525643251394?text=${message}`, '_blank');
  }

  // Don't show controls in print mode
  const showControls = !printMode;

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border-0">
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 to-indigo-500 rounded-t-lg"></div>

        <CardHeader className="py-4 px-6 bg-gradient-to-r from-indigo-50 to-purple-50/50 border-b border-indigo-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-indigo-100 border border-indigo-200 shadow-sm flex items-center justify-center">
                <Stethoscope className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-indigo-800">
                  Diagnósticos a Descartar ({diagnosticosADescartar.length})
                </CardTitle>
                <div className="text-sm text-indigo-600/80 flex items-center flex-wrap gap-x-4 gap-y-1 mt-1">
                  {urgentCount > 0 && (
                    <span className="flex items-center gap-1.5">
                      <AlertOctagon className="h-3.5 w-3.5 text-red-500" />
                      <span>{urgentCount} {urgentCount === 1 ? 'urgente' : 'urgentes'}</span>
                    </span>
                  )}
                  {monitorCount > 0 && (
                    <span className="flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />
                      <span>{monitorCount} {monitorCount === 1 ? 'para monitorear' : 'para monitorear'}</span>
                    </span>
                  )}
                  {normalCount > 0 && (
                    <span className="flex items-center gap-1.5">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                      <span>{normalCount} {normalCount === 1 ? 'normal' : 'normales'}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Controls */}
            {showControls && (
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handlePrint}
                        className="text-xs border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                      >
                        <Printer className="h-3.5 w-3.5 mr-1" />
                        Imprimir
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Imprimir diagnósticos</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleAllDiagnostics(true)}
                        className="text-xs border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                      >
                        <ListChecks className="h-3.5 w-3.5 mr-1" />
                        Ver todos
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Expandir todos los diagnósticos</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleAllDiagnostics(false)}
                        className="text-xs border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                      >
                        <ChevronUp className="h-3.5 w-3.5 mr-1" />
                        Colapsar
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Colapsar todos los diagnósticos</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Decorative pattern */}
          <div className="absolute right-4 top-20 opacity-5">
            <PawPrint className="h-32 w-32 text-indigo-400 transform rotate-12" />
          </div>

          {/* Filter tabs for larger sets of diagnostics */}
          {showControls && diagnosticosADescartar.length > 3 && (
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-100">
              <Tabs defaultValue="todos" value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-white border border-gray-200 p-1">
                  <TabsTrigger value="todos" className="text-xs data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
                    Todos ({diagnosticosADescartar.length})
                  </TabsTrigger>

                  {urgentCount > 0 && (
                    <TabsTrigger value="urgentes" className="text-xs data-[state=active]:bg-red-50 data-[state=active]:text-red-700">
                      <AlertOctagon className="h-3 w-3 mr-1" />
                      Urgentes ({urgentCount})
                    </TabsTrigger>
                  )}

                  {monitorCount > 0 && (
                    <TabsTrigger value="monitorear" className="text-xs data-[state=active]:bg-yellow-50 data-[state=active]:text-yellow-700">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Monitorear ({monitorCount})
                    </TabsTrigger>
                  )}

                  {normalCount > 0 && (
                    <TabsTrigger value="normales" className="text-xs data-[state=active]:bg-green-50 data-[state=active]:text-green-700">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Normales ({normalCount})
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            </div>
          )}

          {/* Quick Navigation Pills - For many diagnostics */}
          {showControls && diagnosticosADescartar.length > 6 && (
            <div className="flex flex-wrap gap-2 p-4 bg-white border-b">
              {diagnosticosADescartar.map((diagnostico, idx) => {
                const config = urgencyConfig[diagnostico.urgencia] || urgencyConfig.verde;

                // Skip if filtered out
                if (activeTab !== "todos") {
                  if (activeTab === "urgentes" && diagnostico.urgencia !== 'rojo' && diagnostico.urgencia !== 'naranja') return null;
                  if (activeTab === "monitorear" && diagnostico.urgencia !== 'amarillo') return null;
                  if (activeTab === "normales" && diagnostico.urgencia !== 'verde') return null;
                }

                return (
                  <button
                    key={`summary-${idx}`}
                    onClick={() => {
                      toggleDiagnostic(idx);
                      // Auto-scroll to the item
                      setTimeout(() => {
                        document.getElementById(`diagnostic-${idx}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 100);
                    }}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-all",
                      config.badge,
                      openDiagnostics[idx] ? "ring-2 ring-offset-1" : "opacity-80 hover:opacity-100"
                    )}
                  >
                    {config.pulseAnimation && (
                      <span className="relative flex h-2 w-2 mr-1">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                      </span>
                    )}
                    {diagnostico.condicion}
                  </button>
                );
              })}
            </div>
          )}

          {/* No Results Message */}
          {filteredDiagnostics.length === 0 && (
            <div className="p-8 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <Search className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-700">No hay diagnósticos en esta categoría</h3>
              <p className="text-gray-500 mt-1">Cambia el filtro para ver otros diagnósticos</p>
            </div>
          )}

          {/* Individual Diagnostic Items */}
          {filteredDiagnostics.map((diagnostico, idx) => {
            const config = urgencyConfig[diagnostico.urgencia] || urgencyConfig.verde;
            // Find the original index in the full array for correct toggling
            const originalIndex = diagnosticosADescartar.findIndex(d => d === diagnostico);

            return (
              <div id={`diagnostic-${originalIndex}`} key={originalIndex}>
                <DiagnosticoItem
                  diagnostico={diagnostico}
                  config={config}
                  isOpen={openDiagnostics[originalIndex]}
                  toggleOpen={() => toggleDiagnostic(originalIndex)}
                  index={originalIndex}
                  printMode={printMode}
                />
              </div>
            );
          })}
        </CardContent>

        {/* Card Footer with Summary */}
        {showControls && diagnosticosADescartar.length > 3 && (
          <CardFooter className="p-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-sm text-gray-500">
              Mostrando {filteredDiagnostics.length} de {diagnosticosADescartar.length} diagnósticos
            </div>

            <Button
              variant="outline"
              className="text-xs border-indigo-200 text-indigo-600 hover:bg-indigo-50"
              onClick={() => setActiveTab("todos")}
              disabled={activeTab === "todos"}
            >
              <Filter className="h-3.5 w-3.5 mr-1" />
              Ver todos los diagnósticos
            </Button>
          </CardFooter>
        )}
      </Card>

      {/* Alerta condicional para consulta veterinaria */}
      {hasUrgentDiagnostics && (
        <Alert className="bg-gradient-to-r from-orange-50 to-orange-50/50 border border-orange-200 rounded-xl shadow-sm">
          <div className="flex items-start gap-3 w-full">
            <div className="h-10 w-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-orange-200 flex-shrink-0">
              <Stethoscope className="h-5 w-5 text-orange-500" />
            </div>
            <AlertDescription className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 w-full">
              <span className="text-orange-700 text-sm font-medium">
                Nuestros veterinarios están disponibles para resolver tus dudas sobre estos resultados
              </span>
              <Button
                onClick={onSubmit}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-sm w-full sm:w-auto transition-all duration-300"
                size="sm"
              >
                <span className="text-sm font-medium">Agendar revisión en línea</span>
                <MessageCircle className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </div>
        </Alert>
      )}

      <UpgradeModalVetConsult
        isOpen={showUpgradeModalVetConsult}
        onClose={() => setShowUpgradeModalVetConsult(false)}
      />
    </div>
  );
};

export default DiagnosticosCard;